import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "./session.js";
import { isRole } from "../roles.js";

/**
 * Public landing the IdP handoff returns to (E8.2). The server callback (/api/v1/auth/callback)
 * issued a short-lived session token and passed it here in the URL fragment; we populate the
 * SessionProvider and continue to the intended destination. Reading from the fragment (not the
 * query) keeps the token out of server access logs and the Referer header. A malformed/absent
 * payload falls back to /login rather than leaving the user on a dead page.
 */
const DEFAULT_DESTINATION = "/portfolio";

function safeDestination(raw: string | null): string {
  if (raw && raw.startsWith("/") && !raw.startsWith("//")) return raw;
  return DEFAULT_DESTINATION;
}

export function AuthCallback(): React.ReactNode {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const { hash } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const token = params.get("token");
    const subject = params.get("subject");
    const tenant = params.get("tenant");
    const role = params.get("role");
    if (token && subject && tenant && isRole(role)) {
      signIn({ subject, tenant, role, token });
      navigate(safeDestination(params.get("redirect")), { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  }, [hash, signIn, navigate]);

  return (
    <main style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <p className="lab">Signing you in…</p>
    </main>
  );
}
